/**
 * Conversations management hook.
 *
 * Manages conversation and message state for the partner finder messaging feature.
 * Handles fetching messages, creating conversations, sending messages, marking
 * as read, and deleting conversations. Provides conversation selection and
 * message threading functionality.
 *
 * @module useConversations
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Conversation, Message, GermanLevel } from '@/types/partner-finder';
import { getMyMessages, sendMessage, markMessageAsRead, deleteMessage, getAllPartnerRequests } from '@/lib/partnerApi';
import type { MessageAPI, SessionCount } from '@/types/api/partner';
import { mapMessagesForConversation } from '@/lib/mappers';
import { aggregateMessagesToConversations } from '@/lib/conversationService';
import { toast } from 'sonner';
import { useAsyncData } from './useAsyncData';
import { useTranslation } from 'react-i18next';

interface UseConversationsOptions {
  readonly userId?: string;
  readonly initialConversationId?: string | null;
}

interface UseConversationsReturn {
  readonly conversations: Conversation[];
  readonly allMessages: MessageAPI[];
  readonly selectedConversationId: string | null;
  readonly isLoading: boolean;
  readonly selectedConversation: Conversation | null;
  readonly currentMessages: Message[];
  readonly messagesRemaining: number | null;
  readonly setSelectedConversationId: (id: string | null) => void;
  readonly handleSendMessage: (content: string) => Promise<void>;
  readonly handleMarkUnread: (conversationId: string) => Promise<void>;
  readonly handleDeleteConversation: (conversationId: string) => Promise<void>;
  readonly handleConversationSelect: (conversationId: string) => void;
  readonly loadMessages: () => Promise<void>;
}

/**
 * Custom hook for managing conversations and messages
 */
export function useConversations({
  userId,
  initialConversationId = null,
}: UseConversationsOptions): UseConversationsReturn {
  const { t } = useTranslation();
  const location = useLocation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [allMessages, setAllMessages] = useState<MessageAPI[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    initialConversationId,
  );
  const [messagesRemaining, setMessagesRemaining] = useState<number | null>(null);

  // Store partner info from navigation state for new conversations
  const partnerInfoFromState = location.state as
    | { partnerId?: string; partnerName?: string; partnerLevel?: string }
    | null;
  const hasProcessedStateRef = useRef(false);

  // Fetch messages, session counts, and active partner request user IDs in parallel
  interface ConversationFetchResult {
    messages: MessageAPI[];
    sessionCounts: SessionCount[];
    activePartnerIds: Set<string> | null;
  }

  const { data: fetchResult, isLoading, refetch } = useAsyncData<ConversationFetchResult>(
    async () => {
      if (!userId) {
        return { messages: [], sessionCounts: [], activePartnerIds: null };
      }
      const [{ messages: msgs, sessionCounts }, activeRequests] = await Promise.all([
        getMyMessages(userId),
        getAllPartnerRequests().catch(() => null),
      ]);
      return {
        messages: msgs,
        sessionCounts,
        activePartnerIds: activeRequests
          ? new Set(activeRequests.map((r) => r.userId))
          : null,
      };
    },
    [userId],
  );

  // Process fetched data and update state
  useEffect(() => {
    const messages = fetchResult?.messages;
    const sessionCounts = fetchResult?.sessionCounts ?? [];
    const activePartnerIds = fetchResult?.activePartnerIds ?? null;

    if (!messages) {
      setAllMessages([]);
      setConversations([]);
      return;
    }

    setAllMessages(messages);
    const convs = userId
      ? aggregateMessagesToConversations(messages, userId, activePartnerIds, sessionCounts)
      : [];

    // Check if we need to create a new conversation from navigation state
    // Only process once per navigation
    if (partnerInfoFromState?.partnerId && userId && !hasProcessedStateRef.current) {
      const conversationId = `conv-${partnerInfoFromState.partnerId}`;
      const existingConv = convs.find((c) => c.id === conversationId);

      if (!existingConv) {
        // Create a new conversation entry for the partner
        const newConversation: Conversation = {
          id: conversationId,
          partnerId: partnerInfoFromState.partnerId,
          partnerName: partnerInfoFromState.partnerName || 'Unknown',
          partnerLevel: (partnerInfoFromState.partnerLevel as GermanLevel) || 'B1',
          lastMessage: undefined,
          lastMessageTime: undefined,
          unreadCount: 0,
          updatedAt: new Date().toISOString(),
          partnerHasActiveRequest:
            activePartnerIds === null ||
            activePartnerIds.has(partnerInfoFromState.partnerId),
        };
        convs.unshift(newConversation); // Add to beginning of list
      }

      // Mark as processed and auto-select the conversation
      hasProcessedStateRef.current = true;
      setSelectedConversationId(conversationId);
    } else if (!selectedConversationId && convs.length > 0) {
      // Auto-select first conversation if none selected
      setSelectedConversationId(convs[0].id);
    }

    setConversations(convs);

    // Sync messagesRemaining for the currently selected conversation after every fetch
    if (selectedConversationId) {
      const current = convs.find((c) => c.id === selectedConversationId);
      setMessagesRemaining(current?.sessionInfo?.remaining ?? null);
    }
  }, [fetchResult, userId, partnerInfoFromState, selectedConversationId]);

  // Reset the processed flag when location changes (new navigation)
  useEffect(() => {
    hasProcessedStateRef.current = false;
  }, [location.key]);

  // Function to manually reload messages (for refresh scenarios)
  const loadMessages = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const selectedConversation =
    conversations.find((c) => c.id === selectedConversationId) || null;
  const currentMessages =
    selectedConversationId && userId
      ? mapMessagesForConversation(allMessages, selectedConversationId, userId)
      : [];

  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    const conv = conversations.find((c) => c.id === conversationId);
    setMessagesRemaining(conv?.sessionInfo?.remaining ?? null);
    // Mark conversation as read when selected
    if (conv && conv.unreadCount > 0) {
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)),
      );
      // Mark messages as read via API
      const unreadMessages = allMessages.filter(
        (msg) =>
          msg.toUserId === userId &&
          !msg.isRead &&
          (msg.fromUserId === conv.partnerId || msg.toUserId === conv.partnerId),
      );
      unreadMessages.forEach((msg) => markMessageAsRead(msg.id));
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!selectedConversation || !userId) return;

    const result = await sendMessage({
      fromUserId: userId,
      toUserId: selectedConversation.partnerId,
      content,
    });

    if (result) {
      toast.success(t('partnerFinder.messages.sentSuccess', { defaultValue: 'Message sent successfully' }));
      setMessagesRemaining(result.messagesRemaining);

      // Add message to local state
      const newMessage: MessageAPI = {
        ...result.message,
        fromUserId: userId,
        fromUserName: '', // Will be updated on reload
        toUserId: selectedConversation.partnerId,
        toUserName: selectedConversation.partnerName,
      };
      setAllMessages((prev) => [...prev, newMessage]);

      // Update conversation - ensure it exists in the list
      setConversations((prev) => {
        const existingConv = prev.find((c) => c.id === selectedConversationId);
        if (existingConv) {
          // Update existing conversation
          return prev.map((conv) =>
            conv.id === selectedConversationId
              ? {
                  ...conv,
                  lastMessage: content,
                  lastMessageTime: 'Just now',
                  updatedAt: new Date().toISOString(),
                }
              : conv,
          );
        } else {
          // Add new conversation if it doesn't exist (shouldn't happen, but safety check)
          return [
            {
              ...selectedConversation,
              lastMessage: content,
              lastMessageTime: 'Just now',
              updatedAt: new Date().toISOString(),
            },
            ...prev,
          ];
        }
      });

      // Reload messages to get the latest state from server
      await refetch();
    }
  };

  const handleMarkUnread = async (conversationId: string) => {
    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation || !userId) return;

    // Toggle between read/unread
    if (conversation.unreadCount > 0) {
      // Mark as read: find all unread messages and mark them as read via API
      const partnerId = conversation.partnerId;
      const unreadMessages = allMessages.filter(
        (msg) =>
          msg.toUserId === userId &&
          !msg.isRead &&
          (msg.fromUserId === partnerId || msg.toUserId === partnerId),
      );

      if (unreadMessages.length > 0) {
        // Mark all unread messages as read
        await Promise.all(unreadMessages.map((msg) => markMessageAsRead(msg.id)));
      }

      // Reload messages to update UI
      await loadMessages();
    } else {
      // Mark as unread: just increment count in UI (no API call needed)
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, unreadCount: conv.unreadCount + 1 }
            : conv,
        ),
      );
    }
  };

  // Helper: Get messages for a conversation
  const getConversationMessages = (partnerId: string, currentUserId: string) => {
    return allMessages.filter(
      (msg) =>
        (msg.fromUserId === partnerId && msg.toUserId === currentUserId) ||
        (msg.fromUserId === currentUserId && msg.toUserId === partnerId),
    );
  };

  // Helper: Clear selected conversation if it matches
  const clearSelectedConversationIfNeeded = (conversationId: string) => {
    if (selectedConversationId === conversationId) {
      setSelectedConversationId(null);
    }
  };

  // Helper: Delete messages and return counts
  const deleteMessages = async (
    messages: MessageAPI[],
  ): Promise<{ deletedCount: number; failedCount: number }> => {
    let deletedCount = 0;
    let failedCount = 0;

    for (const message of messages) {
      const deleted = await deleteMessage(message.id);
      if (deleted) {
        deletedCount++;
      } else {
        failedCount++;
      }
    }

    return { deletedCount, failedCount };
  };

  // Helper: Show toast notification for deletion results
  const showDeletionToast = (
    deletedCount: number,
    totalCount: number,
    failedCount: number,
  ) => {
    if (failedCount > 0) {
      toast.warning(
        `Deleted ${deletedCount} of ${totalCount} message${totalCount > 1 ? 's' : ''} (${failedCount} failed)`,
      );
    } else {
      toast.success(`Deleted ${deletedCount} message${deletedCount > 1 ? 's' : ''}`);
    }
  };

  // Helper: Handle empty conversation deletion
  const handleEmptyConversationDeletion = (conversationId: string) => {
    setConversations((prev) => prev.filter((conv) => conv.id !== conversationId));
    clearSelectedConversationIfNeeded(conversationId);
  };

  const handleDeleteConversation = async (conversationId: string) => {
    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation || !userId) return;

    const conversationMessages = getConversationMessages(conversation.partnerId, userId);

    if (conversationMessages.length === 0) {
      handleEmptyConversationDeletion(conversationId);
      return;
    }

    const { deletedCount, failedCount } = await deleteMessages(conversationMessages);

    if (deletedCount > 0) {
      clearSelectedConversationIfNeeded(conversationId);
      await loadMessages();
      showDeletionToast(deletedCount, conversationMessages.length, failedCount);
    } else {
      toast.error('Failed to delete conversation');
    }
  };

  return {
    conversations,
    allMessages,
    selectedConversationId,
    isLoading,
    selectedConversation,
    currentMessages,
    messagesRemaining,
    setSelectedConversationId,
    handleSendMessage,
    handleMarkUnread,
    handleDeleteConversation,
    handleConversationSelect,
    loadMessages,
  };
}

